import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { DndContext, KeyboardSensor, useSensor, useSensors, type Announcements, type CollisionDetection, type DragEndEvent, type DragStartEvent, type KeyboardCoordinateGetter, type UniqueIdentifier } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { cx } from "../utils/format";
import { insertionLine, keyStep, landingIndex } from "../utils/sortableLanding";
import { DragLayer, QuickClickPointerSensor, EDGE, Handle, holdEarlyKeys, INSTRUCTIONS, KEYS, PICK_UP_AFTER, stayInPlace, useEscapeClaim } from "./sortableParts";

/** The kit's drag and drop for rows, on dnd-kit (the form embed round; the founder, 2026-10-26: "use the dnd-kit library anyway; we want
 *  something properly tested and developed"). One list per `SortableList`; the screen draws its rows through the render prop: each row spreads
 *  `rowProps(index)`, each draggable row shows `handle(index, name)` ("Drag to reorder <name>"). The drop answers `onDrop(from, to)` (indexes
 *  into `ids`) and the screen saves it.
 *
 *  The look stays the editor round's: the rows stay where they are while one is carried (dnd-kit's slide transforms are never applied, the
 *  sorting strategy computes none), the carried row at its place dimmed to 40 %, a ghost (a small box with the row's handle mark and name,
 *  never a copy of a table row) follows the pointer through dnd-kit's overlay, and an insertion line in the accent marks where the row lands;
 *  nothing slides or glides (no drop animation, no overlay transition, instant keyboard scrolling). Where a row lands is ours
 *  (`sortableLanding.ts`): the half of the row under the pointer decides, a row dropped past the end moves to the last place.
 *
 *  Sensors: the pointer (mouse, pen and finger, on the handle only, which takes no page scroll; a few pixels of movement start the drag, no
 *  touch delay) and the keyboard (Space picks up and drops, the arrows move one place, Escape cancels; Enter does nothing). Screen readers hear
 *  the app's words through dnd-kit's live region, never its English defaults. Escape during a drag is claimed in the capture phase
 *  (`preventDefault`), so a dialog around the list (the kit's `Dialog` ignores a claimed Escape) stays open while the drag is cancelled. One
 *  `DndContext` per list. Groups whose entries move within and across them (the Customize dialog) use the nested mode instead,
 *  `SortableGroups` (the sidebar editor rework, 2026-10-26: one drag context for groups and entries, superseding "nested lists never trade
 *  rows"); the parts both share live in `sortableParts.tsx`. */
export interface SortableRows {
  /** the attributes of the row at `index`: its mark for the handle and its class (with yours) */
  rowProps: (index: number, className?: string) => { "data-drag-row": ""; className: string | undefined };
  /** the handle for the row at `index` */
  handle: (index: number, name: string) => ReactNode;
}

type Carry = { from: number; at: number; name: string; width: number };

export function SortableList({ ids, onDrop, children }: { ids: readonly string[]; onDrop: (from: number, to: number) => void; children: (rows: SortableRows) => ReactNode }) {
  const list = useRef({ ids, rows: new Map<string, HTMLElement>(), names: new Map<string, string>(), from: -1, at: -1, keyboard: false });
  list.current.ids = ids;
  const [carry, setCarry] = useState<Carry | null>(null);
  const [line, setLine] = useState<ReturnType<typeof insertionLine>>(null);
  const drop = useRef(onDrop); drop.current = onDrop;
  const prefix = useId();
  const keyboardStep = useKeyboardStep(list);
  const landing = useLanding(list);
  const sensors = useSensors(
    useSensor(QuickClickPointerSensor, { activationConstraint: { distance: PICK_UP_AFTER } }),
    useSensor(KeyboardSensor, { keyboardCodes: KEYS, coordinateGetter: keyboardStep, scrollBehavior: "auto", onActivation: holdEarlyKeys }),
  );
  const rects = () => { const l = list.current; return l.ids.map((id) => l.rows.get(id)?.getBoundingClientRect()).filter((r): r is DOMRect => !!r); };
  const aim = () => { const l = list.current; setCarry((c) => (c && c.at !== l.at ? { ...c, at: l.at } : c)); setLine(insertionLine(rects(), l.from, l.at)); };
  const end = (e: DragEndEvent | null) => {
    const { from, at } = list.current;
    list.current.from = -1; setCarry(null); setLine(null);
    if (e && from >= 0 && at !== from) drop.current(from, at);
  };
  useEscapeClaim(carry !== null);

  const rows: SortableRows = {
    rowProps: (index, className) => ({ "data-drag-row": "", className: cx(className, carry?.from === index && "ob-row-drag--moving") || undefined }),
    handle: (index, name) => { const id = ids[index]!; list.current.names.set(id, name); return <Handle key={`${prefix}${id}`} id={id} name={name} rows={list.current.rows} />; },
  };
  return (
    <DndContext sensors={sensors} collisionDetection={landing} autoScroll={{ threshold: { x: 0, y: EDGE } }}
      accessibility={{ announcements: wordsOf(list), screenReaderInstructions: { draggable: INSTRUCTIONS } }}
      onDragStart={(e: DragStartEvent) => {
        const l = list.current; const id = String(e.active.id);
        l.from = l.at = l.ids.indexOf(id); l.keyboard = e.activatorEvent instanceof KeyboardEvent;
        setCarry({ from: l.from, at: l.from, name: l.names.get(id) ?? "", width: Math.min(l.rows.get(id)?.getBoundingClientRect().width ?? 320, 480) });
      }}
      onDragMove={aim} onDragOver={aim} onDragEnd={end} onDragCancel={() => end(null)}>
      <SortableContext items={ids as string[]} strategy={stayInPlace}>{children(rows)}</SortableContext>
      <DragLayer carry={carry} line={line} />
    </DndContext>
  );
}

type ListRef = { current: { ids: readonly string[]; rows: Map<string, HTMLElement>; names: Map<string, string>; from: number; at: number; keyboard: boolean } };

/** dnd-kit's collision step, used to work out the landing place: the pointer's place over the rows measured right now (`landingIndex`), or
 *  the keyboard's place; it keeps that place on the list (`at`) and names the row it points at */
function useLanding(list: ListRef): CollisionDetection {
  return useCallback(({ pointerCoordinates }) => {
    const l = list.current;
    if (l.from < 0) return [];
    if (pointerCoordinates) {
      const boxes = l.ids.map((id) => l.rows.get(id)?.getBoundingClientRect());
      if (boxes.every((b): b is DOMRect => !!b)) l.at = landingIndex(boxes, pointerCoordinates.y, l.from);
    }
    const id = l.ids[l.at];
    return id ? [{ id }] : [];
  }, [list]);
}

/** an arrow key while a row is picked up by the keyboard: one place up or down (`keyStep`), the ghost moved onto that row */
function useKeyboardStep(list: ListRef): KeyboardCoordinateGetter {
  return useCallback((event, { currentCoordinates }) => {
    const l = list.current;
    const next = keyStep(l.at, event.code, l.ids.length);
    if (next === null || l.from < 0) return undefined;
    event.preventDefault();
    if (next === l.at) return undefined;
    const [was, now] = [l.rows.get(l.ids[l.at]!)?.getBoundingClientRect(), l.rows.get(l.ids[next]!)?.getBoundingClientRect()];
    l.at = next;
    return { x: currentCoordinates.x, y: currentCoordinates.y + (now && was ? now.top - was.top : 0) };
  }, [list]);
}

/** what screen readers hear during a keyboard drag, in the app's words: the row's name and its place (a pointer drag is silent, as before:
 *  the person sees where the row goes) */
function wordsOf(list: ListRef): Announcements {
  const name = (id: UniqueIdentifier) => list.current.names.get(String(id)) ?? "";
  const count = () => list.current.ids.length;
  const say = (words: string) => (list.current.keyboard ? words : undefined);
  return {
    onDragStart: ({ active }) => say(`${name(active.id)}: place ${list.current.from + 1} of ${count()}. The arrow keys move it, Space drops it, Escape puts it back.`),
    onDragOver: ({ active }) => say(`${name(active.id)}: place ${list.current.at + 1} of ${count()}.`),
    onDragEnd: ({ active }) => say(list.current.at !== list.current.from ? `${name(active.id)} moved to place ${list.current.at + 1}.` : `${name(active.id)} stays where it was.`),
    onDragCancel: ({ active }) => say(`${name(active.id)} stays where it was.`),
  };
}

/** the header cell above a table's handles (its words only for screen readers) */
export function SortableHead() {
  return <th className="ob-row-drag"><span className="ob-sr-only">Order</span></th>;
}
/** the table cell that holds a row's handle */
export function SortableCell({ children }: { children: ReactNode }) {
  return <td className="ob-row-drag">{children}</td>;
}
